import type { FilterDTO, VehicleTypeDTO } from "../../search";
import type { SearchFilterDTO } from "../../search/dtos/api/search-filters.dto";
import { BodyTypeTransformer } from "./body-type-filter.transformer";
import { CapitalizeFilterTransformer } from "./capitalize-filter-label.transformer";
import { FuelTransformer } from "./fuel-filter.transformer";
import { OfferTransformer } from "./offers-filter.transformer";
import { MostUsedTransformer } from "./most-used-filter.transformer";
import { OpportunitiesTransformer } from "./opportunities-filter.transformer";
import { OptionalTransformer } from "./optional-filter.transformer";
import {
  ReorderFilterTransformer,
  type ReorderFilterTransformOptions,
} from "./reorder-filter.transformer";
import { SellerTypeTransformer } from "./seller-type-filter.transformer";
import { TransmissionTransformer } from "./transmission-filter.transformer";

export abstract class FilterTransformer {
  /**
   * Transforms a list of search filters by applying specific transformations based on each filter's name.
   * It also reorders the filters according to the provided vehicle type.
   *
   * @param {SearchFilterDTO[]} filters - The list of search filters to transform.
   * @param {VehicleTypeDTO} vehicleType - The type of vehicle to be used for filter reordering.
   * @param {ReorderFilterTransformOptions} [reorderOptions] - Optional reorder behavior (e.g. grouped filters).
   * @returns {FilterDTO[]} - The transformed and reordered list of filters.
   */
  static transform(
    filters: SearchFilterDTO[],
    vehicleType: VehicleTypeDTO,
    reorderOptions?: ReorderFilterTransformOptions
  ): FilterDTO[] {
    const reorderedFilters = ReorderFilterTransformer.transform(
      filters,
      vehicleType,
      reorderOptions
    );

    return reorderedFilters.map((filter) => {
      switch (filter.name) {
        case "SellerType":
          return SellerTypeTransformer.transform(filter);
        case "BodyType":
          return BodyTypeTransformer.transform(filter);
        case "Fuel":
          return FuelTransformer.transform(filter);
        case "OP":
          return OptionalTransformer.transform(filter);
        case "Transmission":
          return TransmissionTransformer.transform(filter);
        case "HotDeal":
          return OfferTransformer.transform(filter);
        case "Opportunities":
          return OpportunitiesTransformer.transform(filter);
        case "MostUsed":
          return MostUsedTransformer.transform(filter);
        case "Break":
        case "Cooling":
        case "Shift":
        case "Starter":
          return CapitalizeFilterTransformer.transform(filter);
        default:
          return filter;
      }
    });
  }
}
